import { useCallback, useEffect, useMemo, useState } from "react";
import useSWRInfinite, { type SWRInfiniteConfiguration } from "swr/infinite";
import * as Sentry from "@sentry/react";
import { isAxiosError } from "axios";
import { useApiClient } from "@/hooks/useApiClient";
import {
  type AcquireBookStatus,
  type AcquireBookStatusResponse,
} from "../types";
import { validatePictureRequestDetailContract } from "../utils/pictureRequestContract";
import { retryUnlessContractViolation } from "../utils/swrErrorRetry";

// 大量明細（数万件）の全件取得を1リクエストで行うと
// BE のシリアライズがワーカーを長時間占有するため、分割して取得して結合する
export const CHUNK_SIZE = 5000;

/**
 * 分割取得の SWR オプション。
 * - parallel false: 同時発行で BE のワーカーを埋めないよう1チャンクずつ取る。
 * - revalidateFirstPage false: チャンクを1つ増やすたびに先頭を取り直さない。
 * - revalidateOnFocus/Reconnect false: 明細は取得完了時の読み込み直しでのみ更新する。
 * - onErrorRetry: 応答契約違反だけ再試行を止め、一過性の失敗は既定の回数・間隔で再試行する
 *   （依頼詳細のポーリングと共通の retryUnlessContractViolation）。
 * - onError: 400系のバリデーション等を除く予期しない失敗のみ Sentry へ送る。
 */
const CHUNKED_DETAILS_SWR_OPTIONS: SWRInfiniteConfiguration = {
  parallel: false,
  revalidateFirstPage: false,
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
  onErrorRetry: retryUnlessContractViolation,
  onError: (error: unknown) => {
    if (
      isAxiosError(error) &&
      (error.response == null || error.response.status >= 500)
    ) {
      Sentry.captureException(error);
    }
  },
};

interface UseChunkedBookDetails {
  /** 全チャンク結合済みの明細。読み込み完了まで null */
  list: AcquireBookStatus[] | null;
  loadedCount: number;
  isLoading: boolean;
  error: Error | null;
  /** 明細を最初から読み込み直す（取得完了時の反映用） */
  reload: () => void;
}

/**
 * 明細を limit/offset で分割取得して全件のリストを組み立てる。
 * 有効化された時点の総件数分を読み込む
 */
export const useChunkedBookDetails = (
  id: string | undefined,
  enabled: boolean,
  totalCount: number | undefined
): UseChunkedBookDetails => {
  const { apiClient } = useApiClient();

  // NOTE: チャンク数に定数の上限（MAX_CHUNKS など）は設けない。クライアント上限は
  // 「上限の上限を設けない」PO判断（2026-09-01）で撤廃されており、定数で切ると
  // 正当な大量依頼の明細が黙って欠落する。件数の水増しに対する歯止めは、
  // 下記の最終ページ（CHUNK_SIZE 未満のチャンク）での打ち切りで担保する。
  // 実データが尽きれば必ず短いチャンクが返るため、余分なリクエストは最大1回に収まる
  const chunkCount =
    !enabled || id === undefined || totalCount === undefined
      ? 0
      : Math.ceil(totalCount / CHUNK_SIZE);

  // 読み込み直しは世代を進めてキーを変える。取得済みチャンクを部分的に取り直すと
  // チャンク間で新旧が混ざるため、全チャンクを同じ世代でそろえて取り直す
  const [generation, setGeneration] = useState(0);

  const { data, error, size, setSize } = useSWRInfinite<
    AcquireBookStatusResponse,
    Error
  >(
    (pageIndex): [string, number] | null =>
      id !== undefined && pageIndex < chunkCount
        ? [
            `/picture-request/${id}?limit=${CHUNK_SIZE}&offset=${
              pageIndex * CHUNK_SIZE
            }`,
            generation,
          ]
        : null,
    async ([path]: [string, number]) => {
      const { data: chunk } = await apiClient.get<AcquireBookStatusResponse>(
        path
      );
      validatePictureRequestDetailContract(chunk, path);
      return chunk;
    },
    CHUNKED_DETAILS_SWR_OPTIONS
  );

  // CHUNK_SIZE 未満のチャンクは最終ページなので、総件数（有効化時点の count）に
  // 満たなくてもそこで打ち切る。件数が実データと食い違っていても次のチャンクを要求しない
  const loadedChunkCount = data?.length ?? 0;
  const lastChunkLength = data?.[loadedChunkCount - 1]?.list.length;
  const hasReachedEnd =
    lastChunkLength !== undefined && lastChunkLength < CHUNK_SIZE;

  // 取得済みのチャンクが揃ってから次を要求する
  useEffect(() => {
    if (loadedChunkCount === size && size < chunkCount && !hasReachedEnd) {
      void setSize(size + 1);
    }
  }, [loadedChunkCount, size, chunkCount, hasReachedEnd, setSize]);

  const list = useMemo(
    () =>
      chunkCount > 0 &&
      data != null &&
      (data.length >= chunkCount || hasReachedEnd)
        ? data.flatMap((chunk) => chunk.list)
        : null,
    [data, chunkCount, hasReachedEnd]
  );

  const loadedCount = useMemo(
    () => data?.reduce((count, chunk) => count + chunk.list.length, 0) ?? 0,
    [data]
  );

  const reload = useCallback(() => {
    setGeneration((prev) => prev + 1);
  }, []);

  return {
    list,
    loadedCount,
    isLoading: chunkCount > 0 && list == null && error == null,
    error: error ?? null,
    reload,
  };
};
